// WizardReview — the SUMMARY step every wizard ends on (ADR-038's review
// moment; born with the asset wizard, every wizard ends on it): the steps as
// SECTIONS in their order and under their names, each with "Change" back to its
// step; EVERY fact the wizard asked shows — a ROW with the fact as SET, or the
// quiet dashed mark of one left EMPTY ("Not set"; the register's own word where
// it would flag the fact once saved — "No product yet"), so nobody commits
// without seeing what they left blank; a step whose content is not a list of
// facts (the role's capability picture · the site's responsibilities list — the
// SHARED components, as they are, so the picture evolves on every surface at
// once) fills its section through `content`; under the sections the wizard's
// own strip of what is still open, a SLOT in the wizard's tone: the asset's
// invite (never a gate — ADR-063), the site's activation gate. Composite
// (COMPONENTS §Component taxonomy): the sections, their titles and "Change" are
// the invariant; a section's body and the strip the variants — slots, never
// flags. The jump rides the Stepper's controlled `step`.

import { useId, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../ui/Button.js';
import { DescriptionList, Field } from './DescriptionList.js';
import { ObservationMark } from './observations.js';

export interface WizardReviewRow {
  /** The fact's name — the step's own label for it. */
  label: ReactNode;
  /** The fact as set. Omitted when the row stands for a fact still OPEN (`missing`). */
  value?: ReactNode;
  /** The open fact's word (the register's: "Not set" · "No product yet") — the row carries the dashed mark. */
  missing?: ReactNode;
  /** The value's lighter second line (a qualifier of the fact, never a second fact). */
  detail?: ReactNode;
}

export interface WizardReviewSection {
  id: string;
  /** The step's name — the section's title. */
  title: string;
  /** The step's index — where "Change" goes. */
  step: number;
  rows: WizardReviewRow[];
  /** The word when the step was passed whole ("Skipped — can be added later"); without it an empty section says nothing. */
  skipped?: ReactNode;
  /** A ready-made body in place of the rows — a step's facts that are not a list (the role's capability picture, the
   *  site's responsibilities list): the SHARED component as it is, so the summary and the pages never drift. */
  content?: ReactNode;
}

export interface WizardReviewProps {
  sections: WizardReviewSection[];
  /** "Change" on a section → the host moves its Stepper to that step (the controlled `step`). Absent = no links. */
  onChange?: (step: number) => void;
  /** What is still open, in the wizard's own tone — the asset's `InviteStrip`, the site's gate block. */
  strip?: ReactNode;
  className?: string;
}

export function WizardReview({ sections, onChange, strip, className = '' }: WizardReviewProps) {
  const { t } = useTranslation('common');
  const base = useId();
  return (
    <div className={`space-y-6 ${className}`}>
      {sections.map((s) => {
        const headingId = `${base}-${s.id}`;
        return (
          <section key={s.id} aria-labelledby={headingId}>
            <div className="mb-2 flex items-baseline justify-between gap-3">
              <h3 id={headingId} className="text-sm font-semibold text-[var(--color-text-primary)]">
                {s.title}
              </h3>
              {onChange && (
                <Button variant="link" onClick={() => onChange(s.step)} aria-label={t('wizardReview.changeSection', { section: s.title })}>
                  {t('wizardReview.change')}
                </Button>
              )}
            </div>
            {s.content !== undefined ? (
              s.content
            ) : s.rows.length > 0 ? (
              <DescriptionList>
                {s.rows.map((r, i) => (
                  <Field key={i} label={r.label} detail={r.detail}>
                    {r.missing !== undefined ? (
                      <span className="inline-flex items-center gap-1.5 font-normal text-[var(--color-text-secondary)]">
                        <ObservationMark kind="missing" bare />
                        {r.missing}
                      </span>
                    ) : (
                      r.value
                    )}
                  </Field>
                ))}
              </DescriptionList>
            ) : s.skipped !== undefined ? (
              <p className="text-sm text-[var(--color-text-secondary)]">{s.skipped}</p>
            ) : null}
          </section>
        );
      })}
      {strip}
    </div>
  );
}
